import './playback.css';
import { useEffect, useSyncExternalStore } from 'react';
import { useAuiState } from '@assistant-ui/react';
import { useNamespaceCopy } from '@/i18n/ns';
import copy from '@/i18n/ns/read-aloud';
import { useCompany } from '@/company/context';
import { useWorkspace } from '@/workspace/context';
import { useVoiceConfig } from '../state';
import { speechPlayer } from './player';

export function ReadAloudAction() {
  const strings = useNamespaceCopy(copy);
  const config = useVoiceConfig();
  const workspace = useWorkspace();
  const company = useCompany();
  const messageId = useAuiState((state) => state.message.id);
  useSyncExternalStore(speechPlayer.subscribe, speechPlayer.version, () => 0);
  const companyId = company.data?.company?.id;
  const userId = workspace.user?.id;
  useEffect(() => {
    if (!companyId || !userId || !workspace.threadId || workspace.readOnly) {
      speechPlayer.stop('exit');
      return;
    }
    return speechPlayer.bindScope(
      JSON.stringify([companyId, userId, workspace.threadId, workspace.readOnly]),
    );
  }, [companyId, userId, workspace.threadId, workspace.readOnly]);
  const prefs = config.voice?.prefs;
  const enabled = Boolean(
    !workspace.readOnly &&
    companyId &&
    userId &&
    config.readAloud?.available &&
    config.voice?.policy.readAloud &&
    prefs?.readAloud &&
    prefs.speakWith === 'poneva',
  );
  const mine = speechPlayer.currentMessage() === messageId;
  const state = mine ? speechPlayer.state() : 'idle';
  useEffect(() => {
    if (!enabled && mine) speechPlayer.stop('exit');
  }, [enabled, mine]);
  if (!enabled || !messageId) return null;
  const notice = mine ? speechPlayer.notice() : null;
  const error =
    notice === 'tts_language_unsupported'
      ? strings.noVoiceForLanguage
      : notice === 'tts_budget_exhausted'
        ? strings.errorBudgetExhausted
        : notice === 'payment_required'
          ? strings.errorPaymentRequired
          : notice === 'tap_to_listen'
            ? strings.tapToListen
            : notice
              ? strings.couldNotRead
              : null;
  const running = state === 'speaking' || state === 'loading';
  const label =
    notice === 'tap_to_listen'
      ? strings.tapToListen
      : running
        ? strings.stopReading
        : strings.readAloud;
  const activate = () => {
    if (state === 'paused') {
      void speechPlayer.resume();
      return;
    }
    if (running) {
      speechPlayer.stop('user');
      return;
    }
    void speechPlayer.readMessage(workspace.threadId, messageId, { rate: prefs!.rate });
  };
  return (
    <div className="flex items-center gap-1" data-read-aloud-message={messageId}>
      <button
        type="button"
        aria-label={label}
        onClick={activate}
        className="min-h-11 min-w-11 rounded-md px-3 text-sm"
        data-slot="read-aloud-action"
      >
        {label}
      </button>
      {error && (
        <span role="status" className="text-xs text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}
